import { useEffect, useMemo, useRef, useState } from "react"
import { useInfiniteQuery } from "react-query"
import { useAvailabilityStatus, useWorkTaskId } from "@tm/context-distribution"
import { ArticleErpInfo } from "@tm/models"
import { mapDistinctValues } from "../../../ListV2/helpers"
import { useSortArticles } from "../../../ListV2/hooks/useSortArticles"
import { ArticleType } from "./WheelsListContext"
import { useDefaultErpSystem } from "../../../ListV2/hooks"
import { AvailabilityFilterType } from "../../../../business"
import { filterArticlesByAvailability } from "../../../TyresList/helpers/filterArticlesByAvailability"
import { useParticularContext, WheelsListType } from "../../../PureList/ContextProviders/Particular/ParticularContext"
import { WheelsListData } from "./models"

export function useWheelsList(erpInfos: ArticleErpInfo[]): WheelsListData {
    const contextProvider = useParticularContext<WheelsListType>()
    const { request, selectedAvailability, maxAutoRequest, fetchArticles } = contextProvider ?? {}

    const [filteredArticles, setFilteredArticles] = useState<ArticleType>([])
    const [fetchedXTimes, setFetchedXTimes] = useState(0)
    const isFetchingAutoRef = useRef(false)

    const { availabilityStatusIdsToShow, availabilityStatusIdsToShowSecondary } = useAvailabilityStatus()

    const workTaskId = useWorkTaskId()

    const defaultErpSystemId = useDefaultErpSystem()?.id
    const availabilityFilterChecked = selectedAvailability === AvailabilityFilterType.Primary
    const secondAvailabilityFilterChecked = selectedAvailability === AvailabilityFilterType.Secondary

    const { data, fetchNextPage, hasNextPage, isFetchingNextPage, status, isFetching, isSuccess, isError } = useInfiniteQuery({
        queryKey: ["tyresArticlesList", workTaskId, selectedAvailability, request],
        queryFn: async ({ pageParam = 1 }) => {
            try {
                const response = await fetchArticles!({ ...request!, pageIndex: pageParam })
                return { ...response, pageParam }
            } catch (error) {
                if (error instanceof Error) {
                    // eslint-disable-next-line no-console
                    console.error(error.message)
                }
            }
        },
        getNextPageParam: (lastPage, allPages) => {
            if (!lastPage || !lastPage.hasNextPage) {
                return undefined
            }

            return allPages.length + 1
        },
        enabled: !!request,
    })

    const articles = useMemo(() => data?.pages?.map((x) => x?.articles ?? []).flat(), [data])
    const supplierIds = useMemo(() => (articles?.length ? mapDistinctValues(articles, (x) => x.supplier.id) : []), [articles])

    const applyFilter = () => {
        if (articles?.length) {
            if (selectedAvailability && defaultErpSystemId && availabilityStatusIdsToShow) {
                const filteredData = filterArticlesByAvailability(
                    articles,
                    erpInfos,
                    defaultErpSystemId,
                    availabilityFilterChecked,
                    secondAvailabilityFilterChecked,
                    availabilityStatusIdsToShow,
                    availabilityStatusIdsToShowSecondary
                )
                setFilteredArticles(filteredData)
            } else {
                setFilteredArticles(articles)
            }
        } else {
            setFilteredArticles([])
        }
    }

    useEffect(() => {
        applyFilter()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [articles, selectedAvailability, request])

    useEffect(() => {
        if (!request || !selectedAvailability || isFetchingNextPage || isFetching) {
            return
        }

        if (!hasNextPage || !maxAutoRequest || filteredArticles.length >= request.pageSize || fetchedXTimes >= maxAutoRequest - 1) {
            isFetchingAutoRef.current = false
            return
        }

        isFetchingAutoRef.current = true
        fetchNextPage()
        setFetchedXTimes((prev) => prev + 1)
    }, [
        isFetching,
        isFetchingNextPage,
        selectedAvailability,
        request,
        fetchNextPage,
        hasNextPage,
        maxAutoRequest,
        filteredArticles.length,
        fetchedXTimes,
    ])

    const isLoaded = (isSuccess || isError) && !isFetching

    const { sortedArticleList, selectedSorting, handleSorting } = useSortArticles(filteredArticles || [], erpInfos)

    return useMemo(
        () => ({
            requestStatus: status,
            isEnabled: true,
            isLoading: isFetching,
            isLoaded,
            isSuccess,
            isFailed: isError,
            articles: articles ?? [],
            displayArticles: sortedArticleList,
            articleCount: sortedArticleList.length,
            pageCount: request?.pageIndex ?? 0,
            hasNextPage: !!hasNextPage,
            isFetchingNextPage,
            productGroupIds: [],
            supplierIds,
            loadNextPage: fetchNextPage,
            sorting: { sortedArticleList, selectedSorting, handleSorting },
            isStalling:
                !!selectedAvailability &&
                !sortedArticleList.length &&
                request?.pageIndex === 1 &&
                (!erpInfos?.length || !erpInfos.some((x) => x.state === "success" || x.state === "error")) &&
                hasNextPage,
            // needed so we don't show for half a second is error because the sortedArticleList is not yet set
            isInitialSorting: !selectedAvailability && !sortedArticleList.length && !!articles?.length,
            // needed so we don't fetch 2 pages when we scroll down while loading the first page
            isSorting: !selectedAvailability && sortedArticleList.length !== articles?.length,
            isFetchingAuto: isFetchingAutoRef.current,
        }),
        [
            status,
            isFetching,
            isLoaded,
            isSuccess,
            isError,
            articles,
            sortedArticleList,
            request?.pageIndex,
            hasNextPage,
            isFetchingNextPage,
            supplierIds,
            fetchNextPage,
            selectedSorting,
            handleSorting,
            selectedAvailability,
            erpInfos,
            isFetchingAutoRef,
        ]
    )
}
